import { useState } from "react";
import { Modal } from "./Modal.js";
import { Icon } from "./Icon.js";
import type { CreateGoalBody } from "../api/goals.js";
import type { KidBundle } from "../api/me.js";

const ICON_CHOICES = [
  "target",
  "gamepad",
  "bike",
  "ticket",
  "palette",
  "skate",
  "book",
  "blocks",
  "ball",
  "camera",
  "tent",
  "laptop",
  "car",
];

export interface NewGoalModalProps {
  kid: KidBundle["user"];
  onClose: () => void;
  onCreate: (body: CreateGoalBody) => void | Promise<void>;
}

export function NewGoalModal({ kid, onClose, onCreate }: NewGoalModalProps) {
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("");
  const [icon, setIcon] = useState<string>("target");
  const [allocType, setAllocType] = useState<"$" | "%">("$");
  const [allocValue, setAllocValue] = useState<string>("10");
  const [submitting, setSubmitting] = useState(false);

  const color = kid.color ?? "var(--brand)";

  const submit = async () => {
    const targetNum = Number(target);
    const allocNum = Number(allocValue);
    if (!title || !targetNum || Number.isNaN(targetNum)) return;
    if (Number.isNaN(allocNum)) return;
    setSubmitting(true);
    try {
      const targetCents = Math.round(targetNum * 100);
      const allocCents = allocType === "$" ? Math.round(allocNum * 100) : Math.round(allocNum);
      await onCreate({
        title,
        targetCents,
        icon,
        allocType,
        allocValue: allocCents,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="New goal" onClose={onClose}>
      <div className="stack" style={{ gap: 16 }}>
        <div className="input-group">
          <label className="label">What are you saving for?</label>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Trampoline, New guitar"
            autoFocus
          />
        </div>
        <div className="input-group">
          <label className="label">How much does it cost?</label>
          <input
            className="input"
            type="number"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="100"
          />
        </div>
        <div className="input-group">
          <label className="label">Pick an icon</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {ICON_CHOICES.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: icon === ic ? color : "var(--canvas-warm)",
                  color: icon === ic ? "white" : "var(--ink-soft)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                }}
              >
                <Icon name={ic} size={20} />
              </button>
            ))}
          </div>
        </div>
        <div className="input-group">
          <label className="label">Save how much each month?</label>
          <div style={{ display: "flex", gap: 8 }}>
            <div className="seg">
              <button
                className={allocType === "$" ? "active" : ""}
                onClick={() => setAllocType("$")}
              >
                Fixed $
              </button>
              <button
                className={allocType === "%" ? "active" : ""}
                onClick={() => setAllocType("%")}
              >
                % of income
              </button>
            </div>
            <input
              className="input"
              type="number"
              value={allocValue}
              onChange={(e) => setAllocValue(e.target.value)}
              style={{ width: 100 }}
            />
            <div style={{ display: "flex", alignItems: "center", color: "var(--ink-soft)" }}>
              {allocType === "$" ? "/month" : "%"}
            </div>
          </div>
        </div>
      </div>
      <div className="row" style={{ justifyContent: "flex-end", marginTop: 22 }}>
        <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button className="btn btn-brand" onClick={submit} disabled={submitting}>
          Create goal
        </button>
      </div>
    </Modal>
  );
}
